import isNil from 'lodash/isNil';
import { action, makeObservable, observable } from 'mobx';
import { APIClient as ScaffoldClient, FetchUserResponse, SignOutUserResponse } from '@groma-dev/scaffold';
import { AppConfig } from '../AppConfig';
import { Account, FundType, BankAccount } from '../models';
import { APIClient } from '../apiclient/APIClient';
import {
  FetchAccountDocumentsRequest,
  FetchAccountDocumentsResponse,
  FetchAccountRequest,
  FetchAccountResponse,
  FetchAccountsRequest,
  FetchAccountsResponse,
  FetchAccountTransactionsRequest,
  FetchAccountTransactionsResponse,
  UpdateBankAccountRequest,
  UpdateBankAccountResponse,
} from '../apiclient/APIClientInterface';
import { LocalStorage } from '../utils/Constants';
import {
  AccountDocumentsFetchingStateInterface,
  AccountHoldingsStateInterface,
  AccountsStateInterface,
  AccountStateInterface,
  AccountTransactionsInterface,
  AppState,
  ScaffoldClientHeaders,
  UserStateInterface,
  WalletBalanceStateInterface,
  BankAccountStateInterface,
} from './DataStoreInterface';

const appInitialState: AppState = {
  initialized: false,
  nonREITAccount: false,
};

const userInitialState: UserStateInterface = {
  fetchingUser: false,
  user: undefined,
};

const accountsInitialState: AccountsStateInterface = {
  accounts: undefined,
  isFetching: false,
};

const accountInitialState: AccountStateInterface = {
  account: undefined,
  isFetching: false,
  bankAccount: undefined,
  isBankAccountFetching: false,
};

const accountHoldingsInitialState: AccountHoldingsStateInterface = {
  isFetching: false,
};

const walletBalanceInitialState: WalletBalanceStateInterface = {
  isFetching: false,
};

const accountDocumentsInitialFetchingState: AccountDocumentsFetchingStateInterface = {};

const accountTransactionsInitialState: AccountTransactionsInterface = {
  isFetching: false,
};

const bankAccountInitialState: BankAccountStateInterface = {
  isFetching: false,
};

export default class DataStore {
  /**
   * CLASS VARIABLES
   */
  private static instance: any = null;

  /**
   * MOBX OBSERVABLES
   */

  @observable
  appState = appInitialState;

  @observable
  userState = userInitialState;

  @observable
  accountsState = accountsInitialState;

  @observable
  accountState = accountInitialState;

  @observable
  accountHoldingsState = accountHoldingsInitialState;

  @observable
  walletBalanceState = walletBalanceInitialState;

  @observable
  bankAccountState = bankAccountInitialState;

  @observable
  accountDocumentsFetchingState = accountDocumentsInitialFetchingState;

  @observable
  accountTransactions = accountTransactionsInitialState;

  @observable
  bankAccount = bankAccountInitialState;

  constructor() {
    if (isNil(DataStore.instance)) {
      DataStore.instance = this;
    }

    makeObservable(this);

    // DataStore is a singleton class
    // eslint-disable-next-line no-constructor-return
    return DataStore.instance;
  }

  setLocalStorage = (key: string, value: string) => {
    localStorage.setItem(key, value);
  };

  getLocalStorage = (key: string) => {
    return localStorage.getItem(key);
  };

  /**
   * MOBX ACTIONS
   */
  @action
  setAppInitialized = (value: boolean) => {
    this.appState.initialized = value;
  };

  @action
  fetchUser = async (): Promise<FetchUserResponse> => {
    this.setFetchingUser(true);
    const { baseUrl } = AppConfig.Settings.Server;
    const response: FetchUserResponse = await ScaffoldClient.fetchUser({
      baseURL: baseUrl,
      headers: ScaffoldClientHeaders,
    });

    if (response.success) this.setUser(response.user);

    this.setFetchingUser(false);

    return response;
  };

  @action
  signOutUser = async (): Promise<SignOutUserResponse> => {
    const { baseUrl } = AppConfig.Settings.Server;
    const response = await ScaffoldClient.signOutUser({
      baseURL: baseUrl,
      headers: ScaffoldClientHeaders,
    });
    return response;
  };

  @action
  fetchAccounts = async (request: FetchAccountsRequest): Promise<FetchAccountsResponse> => {
    this.setFetchingAccounts(true);
    const response = await APIClient.fetchAccounts(request);

    return response;
  };

  @action
  fetchAccount = async (request: FetchAccountRequest): Promise<FetchAccountResponse> => {
    this.setFetchingAccount(true);

    const response = await APIClient.fetchAccount(request);
    if (response.success) {
      this.setAccount(response.account);
      this.setLocalStorage(LocalStorage.InvestmentAccount, `${response.account?.id}`);
    }

    this.setFetchingAccount(false);
    this.setAppInitialized(true);

    return response;
  };

  @action
  fetchAccountHoldings = async (request: any) => {
    this.setFetchingAccountHoldings(true);

    const response = await APIClient.fetchAccountHoldings(request);

    this.setFetchingAccountHoldings(false);

    return response;
  };

  @action
  fetchWalletBalance = async (request: any) => {
    this.setFetchingWalletBalance(true);

    const response = await APIClient.fetchWalletBalance(request);

    this.setFetchingWalletBalance(false);

    return response;
  };

  @action
  fetchAccountDocuments = async (request: FetchAccountDocumentsRequest): Promise<FetchAccountDocumentsResponse> => {
    this.setFetchingAccountDocumentsByCat(request.category, true);

    const response = await APIClient.fetchAccountDocuments(request);

    this.setFetchingAccountDocumentsByCat(request.category, false);

    return response;
  };

  @action
  fetchAccountTransactions = async (
    request: FetchAccountTransactionsRequest
  ): Promise<FetchAccountTransactionsResponse> => {
    this.setFetchingAccountTransactions(true);

    const response = await APIClient.fetchAccountTransactions(request);

    this.setFetchingAccountTransactions(false);

    return response;
  };

  @action
  fetchBankAccount = async (request: any) => {
    this.setFetchingBankAccount(true);
    const response = await APIClient.fetchBankAccount(request);

    if (response.success) {
      this.setBankAccount(response.bankAccount);
    } else if (response.status === 404) {
      this.setBankAccount(null);
    }

    this.setFetchingBankAccount(false);

    return response;
  };

  @action
  setBankAccount = (bankAccount: BankAccount | null) => {
    this.accountState.bankAccount = bankAccount;
  };

  @action
  updateBankAccount = async (request: UpdateBankAccountRequest): Promise<UpdateBankAccountResponse> => {
    const response = await APIClient.updateBankAccount(request);

    return response;
  };

  @action
  setFetchingUser = (value: boolean) => {
    this.userState.fetchingUser = value;
  };

  @action
  setUser = (user?: any) => {
    this.userState.user = user;
  };

  @action
  setFetchingAccounts = (value: boolean) => {
    this.accountsState.isFetching = value;
  };

  @action
  setAccounts = (accounts?: Account[]) => {
    this.accountsState.accounts = accounts;
  };

  @action
  setFetchingAccount = (value: boolean) => {
    this.accountState.isFetching = value;
  };

  @action
  setAccount = (account?: Account) => {
    this.accountState.account = account;
    // if account fund is null or is Fund II, set to false
    // otherwise set to NonREIT account
    if (!account?.fund || account?.fund === FundType.FUND_TWO) this.setNonREITAccount(false);
    else this.setNonREITAccount(true);
  };

  @action
  setNonREITAccount = (value: boolean) => {
    this.appState.nonREITAccount = value;
  };

  @action
  setFetchingAccountHoldings = (value: boolean) => {
    this.accountHoldingsState.isFetching = value;
  };

  @action
  setFetchingWalletBalance = (value: boolean) => {
    this.walletBalanceState.isFetching = value;
  };

  @action
  setFetchingAccountDocumentsByCat = (cat: string, value: boolean) => {
    this.accountDocumentsFetchingState[cat] = value;
  };

  @action
  setFetchingAccountTransactions = (value: boolean) => {
    this.accountTransactions.isFetching = value;
  };

  @action
  setFetchingBankAccount = (value: boolean) => {
    this.bankAccount.isFetching = value;
  };
}
